// src/pages/approval/LeaveRequestList.jsx
// ✅ 휴가신청 목록(2026-10-19). [내 신청] = 본인 것만. [전체 보기] = 캘린더 관리자(position_rank 2·3)만 —
// 전 직원 신청서를 월·직원·상태로 거른다. 월 필터의 "승인 합계"는 그 달 근무일 몫만 더한 값이라
// 급여명세서 작성 때 "이번 달 승인된 연차"를 여기서 뽑는다(급여 자동 연동은 백로그). 범위는 서버가 거른다.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { withEndedLabel } from "../../utils/contractEnded";
import LeaveDetailModal from "./LeaveDetailModal";
import styles from "./LeaveRequest.module.css";

const STATES = [
  { value: "", label: "전체 상태" },
  { value: "approved", label: "승인" },
  { value: "pending", label: "결재 대기" },
  { value: "rejected", label: "반려" },
  { value: "approved_canceled", label: "승인(일정 취소됨)" },
];

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function LeaveRequestList() {
  const navigate = useNavigate();
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  }, []);
  const isAdmin = [2, 3].includes(me.position_rank);

  const [scope, setScope] = useState("mine");
  const [month, setMonth] = useState("");
  const [userId, setUserId] = useState("");
  const [state, setState] = useState("");
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/staff-contacts/")
      .then(({ data: d }) => setStaff((Array.isArray(d) ? d : d.results || [])
        .map((u) => ({ id: u.user_id || u.id, label: withEndedLabel(u.name, u.is_contract_ended) }))))
      .catch(() => setStaff([]));
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { scope, month, state };
      if (scope === "all" && userId) params.user = userId;
      const res = await api.get("/draft/leave-requests/", { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "목록을 불러오지 못했습니다.");
      setData({ rows: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [scope, month, userId, state]);

  useEffect(() => { load(); }, [load]);

  const switchScope = (next) => {
    setScope(next);
    if (next === "all" && !month) setMonth(thisMonth());
  };

  return (
    <div className={styles.page}>
      <div className={styles.tabs}>
        <button type="button" className={scope === "mine" ? styles.tabActive : styles.tab}
                onClick={() => switchScope("mine")}>내 신청</button>
        {isAdmin && (
          <button type="button" className={scope === "all" ? styles.tabActive : styles.tab}
                  onClick={() => switchScope("all")}>전체 보기</button>
        )}
        <button type="button" className={styles.smallBtn} style={{ marginLeft: "auto" }}
                onClick={() => navigate("/approval/leave/new")}>휴가신청서 작성</button>
      </div>

      <div className={styles.filters}>
        <input type="month" className={styles.input} value={month} onChange={(e) => setMonth(e.target.value)} />
        {month && <button type="button" className={styles.smallBtn} onClick={() => setMonth("")}>월 해제</button>}
        {scope === "all" && (
          <select className={styles.select} value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">전체 직원</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}
        <select className={styles.select} value={state} onChange={(e) => setState(e.target.value)}>
          {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button type="button" className={styles.smallBtn} onClick={load}>새로고침</button>
      </div>

      {data.summary && (
        <div className={styles.summary}>
          {data.summary.count}건 · 승인 합계 <strong>{data.summary.approved_hours_text}</strong>
          {month ? ` (${month} 근무일 기준)` : ""}
        </div>
      )}
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>문서번호</th><th>작성일</th>{scope === "all" && <th>신청자</th>}<th>휴가구분</th>
              <th>휴가기간</th><th>휴가차감</th>{month && <th>{month} 몫</th>}<th>상태</th><th>결재</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className={styles.empty}>불러오는 중...</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={9} className={styles.empty}>신청서가 없습니다.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.id} className={styles.clickRow} onClick={() => setOpenId(r.id)}>
                <td>{r.draft_no}</td>
                <td>{r.draft_date}</td>
                {scope === "all" && <td>{[r.applicant?.name, r.applicant?.position].filter(Boolean).join(" ")}</td>}
                <td>{r.kind_label}</td>
                <td>{r.period_text}</td>
                <td>{r.hours_text}</td>
                {month && <td>{r.month_hours}시간</td>}
                <td className={styles[`state_${r.state}`]}>{r.state_label}</td>
                <td>
                  {r.decision
                    ? `${r.decision.approver_name}${r.decision.approval_type === "전결" ? "(전결)" : ""}`
                      + (r.decision.result === "rejected" && r.decision.comment ? ` — ${r.decision.comment}` : "")
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId && <LeaveDetailModal documentId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
